import {
  filterAssetsByLocale,
  getAssetByTags,
  getAssetsByTags,
  getAttribute,
  getProductAssets,
  getVariantFinalPrice,
  isVariantActive,
  sortAndDedupeFacets,
  sortAssetsByPriority,
} from './product.util'

/** Creates a view model for a nutrition product */
export function createNutritionViewModel(args: {
  productData: Commercetools.ProductData
  builderContent: any
  locale: string
}): NutritionViewModel {
  filterAssetsByLocale(args.productData, args.locale)
  const productAssets = sortAssetsByPriority(getProductAssets(args.productData))
  const variants = [args.productData.masterVariant]
    .concat(args.productData.variants)
    .map(createNutritionVariant)
    .filter(isVariantActive)
  const masterVariant = variants[0]

  return {
    product: {
      ...args.productData,
      productType: args.productData.productType?.obj?.key ?? '',
      productSubtype: getAttribute(masterVariant, 'productSubtype'),
      productShortDescription: getAttribute(masterVariant, 'productShortDescription'),
      productSeoDescription: getAttribute(masterVariant, 'productSeoDescription'),
      productDirections: getAttribute(masterVariant, 'productDirections'),
      productFeaturedIngredients: getAttribute(masterVariant, 'productFeaturedIngredients'),
      assets: productAssets,
    },
    variants,
    masterVariant,
    selectedVariant: masterVariant,
    accolades: args?.builderContent?.data?.accolades ?? [],
    socialProof: args?.builderContent?.data?.socialProof ?? [],
    alternateProducts: args?.builderContent?.data?.alternateProducts ?? [],
    facetOptions: getNutritionFacetOptions(variants),
    quantity: 1,
  }
}

/** Creates a nutrition variant from a product variant */
function createNutritionVariant(variant: Commercetools.ProductVariant): NutritionVariant {
  return {
    ...variant,
    assets: sortAssetsByPriority(getAssetsByTags(variant, ['type:sku'])),
    originalPrice: variant.prices?.[0]?.value?.centAmount ?? 0,
    finalPrice: getVariantFinalPrice({ variant }),
    facets: {
      flavor: {
        slug: getAttribute(variant, 'flavorSlug'),
        displayName: getAttribute(variant, 'flavorDisplayName'),
        priority: getAttribute(variant, 'flavorPriority'),
        descriptor: getAttribute(variant, 'flavorDescriptor'),
        imagePrimary: getAssetByTags(variant, [
          'type:facet',
          `flavor:${getAttribute(variant, 'flavorSlug')}:primary`,
        ]),
        imageSecondary: getAssetByTags(variant, [
          'type:facet',
          `flavor:${getAttribute(variant, 'flavorSlug')}:secondary`,
        ]),
      },
      packaging: {
        slug: getAttribute(variant, 'packagingSlug'),
        displayName: getAttribute(variant, 'packagingDisplayName'),
        priority: getAttribute(variant, 'packagingPriority'),
        descriptor: getAttribute(variant, 'packagingDescriptor'),
        imagePrimary: getAssetByTags(variant, [
          'type:facet',
          `packaging:${getAttribute(variant, 'packagingSlug')}:primary`,
        ]),
        imageSecondary: getAssetByTags(variant, [
          'type:facet',
          `packaging:${getAttribute(variant, 'packagingSlug')}:secondary`,
        ]),
      },
      // prettier-ignore
      subscriptionFrequency: {
        slug: getAttribute(variant, 'subscriptionFrequencySlug'),
        displayName: getAttribute(variant, 'subscriptionFrequencyDisplayName'),
        marketingName: getAttribute(variant, 'subscriptionFrequencyMarketingName'),
        description: getAttribute(variant, 'subscriptionFrequencyDescription'),
        priority: getAttribute(variant, 'subscriptionFrequencyPriority'),
        priceSuffix: getAttribute(variant, 'subscriptionFrequencyPriceSuffix'),
        bannerText: getAttribute(variant, 'subscriptionFrequencyBannerText'),
        cadence: getAttribute(variant, 'subscriptionFrequencyCadence'),
        imagePrimary: getAssetByTags(variant, [
          'type:facet',
          `subscriptionFrequency:${getAttribute(variant, 'subscriptionFrequencySlug')}:primary`,
        ]),
        imageSecondary: getAssetByTags(variant, [
          'type:facet',
          `subscriptionFrequency:${getAttribute(variant, 'subscriptionFrequencySlug')}:secondary`,
        ]),
      },
      keyIngredient: {
        slug: getAttribute(variant, 'keyIngredientSlug'),
        displayName: getAttribute(variant, 'keyIngredientDisplayName'),
        priority: getAttribute(variant, 'keyIngredientPriority'),
        descriptor: getAttribute(variant, 'keyIngredientDescriptor'),
        imagePrimary: getAssetByTags(variant, [
          'type:facet',
          `keyIngredient:${getAttribute(variant, 'keyIngredientSlug')}:primary`,
        ]),
        imageSecondary: getAssetByTags(variant, [
          'type:facet',
          `keyIngredient:${getAttribute(variant, 'keyIngredientSlug')}:secondary`,
        ]),
      },
    },
  }
}

/** Creates facet options for the nutrition product */
function getNutritionFacetOptions(variants: NutritionVariant[]): NutritionFacetOptions {
  // prettier-ignore
  return {
    flavor: sortAndDedupeFacets({ variants, facetKey: 'flavor' }),
    packaging: sortAndDedupeFacets({ variants, facetKey: 'packaging' }),
    subscriptionFrequency: sortAndDedupeFacets({ variants, facetKey: 'subscriptionFrequency' }),
    keyIngredient: sortAndDedupeFacets({ variants, facetKey: 'keyIngredient' }),
  }
}
